import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  type BaziInput, 
  Gender, 
  TIAN_GAN, 
  DI_ZHI, 
  GAN_ZHI_LIST 
} from "@/lifekline/types";
import { User, Calendar, Sparkles } from "lucide-react";

interface BaziFormProps {
  onSubmit: (input: BaziInput) => void;
  isSubmitting: boolean;
}

export function BaziForm({ onSubmit, isSubmitting }: BaziFormProps) {
  const [formData, setFormData] = useState<Partial<BaziInput>>({
    name: "",
    gender: Gender.MALE,
    birthYear: new Date().getFullYear() - 30 + "",
    yearPillar: "",
    monthPillar: "",
    dayPillar: "",
    hourPillar: "",
    startAge: "8",
    firstDaYun: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: keyof BaziInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user modifies field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.birthYear) {
      newErrors.birthYear = "请输入出生年份";
    }
    if (!formData.yearPillar) {
      newErrors.yearPillar = "请选择年柱";
    }
    if (!formData.monthPillar) {
      newErrors.monthPillar = "请选择月柱";
    }
    if (!formData.dayPillar) {
      newErrors.dayPillar = "请选择日柱";
    }
    if (!formData.hourPillar) {
      newErrors.hourPillar = "请选择时柱";
    }
    if (!formData.startAge) {
      newErrors.startAge = "请输入起运年龄";
    }
    if (!formData.firstDaYun) {
      newErrors.firstDaYun = "请选择第一步大运";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData as BaziInput);
    }
  };

  // Generate pillar options (天干+地支)
  const getPillarOptions = () => {
    return GAN_ZHI_LIST;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <User className="w-5 h-5 text-violet-500" />
          基本信息
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <Label htmlFor="name">姓名（选填）</Label>
            <Input
              id="name"
              value={formData.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="请输入姓名"
            />
          </div>

          {/* Gender */}
          <div>
            <Label>性别</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  checked={formData.gender === Gender.MALE}
                  onChange={() => handleChange("gender", Gender.MALE)}
                  className="w-4 h-4 text-violet-600"
                />
                <span>男 (乾造)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  checked={formData.gender === Gender.FEMALE}
                  onChange={() => handleChange("gender", Gender.FEMALE)}
                  className="w-4 h-4 text-violet-600"
                />
                <span>女 (坤造)</span>
              </label>
            </div>
          </div>

          {/* Birth Year */}
          <div>
            <Label htmlFor="birthYear">出生年份（阳历）</Label>
            <Input
              id="birthYear"
              type="number"
              value={formData.birthYear || ""}
              onChange={(e) => handleChange("birthYear", e.target.value)}
              placeholder="如 1990"
              min={1900}
              max={2100}
            />
            {errors.birthYear && (
              <p className="text-sm text-red-500 mt-1">{errors.birthYear}</p>
            )}
          </div>
        </div>
      </div>

      {/* Four Pillars Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Calendar className="w-5 h-5 text-amber-500" />
          八字四柱
        </h3>
        <p className="text-sm text-muted-foreground">
          请输入您的八字四柱干支，可通过万年历或八字排盘工具获取
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Year Pillar */}
          <div>
            <Label htmlFor="yearPillar">年柱</Label>
            <select
              id="yearPillar"
              value={formData.yearPillar || ""}
              onChange={(e) => handleChange("yearPillar", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">请选择</option>
              {getPillarOptions().map(option => (
                <option key={`year-${option}`} value={option}>{option}</option>
              ))}
            </select>
            {errors.yearPillar && (
              <p className="text-sm text-red-500 mt-1">{errors.yearPillar}</p>
            )}
          </div>

          {/* Month Pillar */}
          <div>
            <Label htmlFor="monthPillar">月柱</Label>
            <select
              id="monthPillar"
              value={formData.monthPillar || ""}
              onChange={(e) => handleChange("monthPillar", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">请选择</option>
              {getPillarOptions().map(option => (
                <option key={`month-${option}`} value={option}>{option}</option>
              ))}
            </select>
            {errors.monthPillar && (
              <p className="text-sm text-red-500 mt-1">{errors.monthPillar}</p>
            )}
          </div>

          {/* Day Pillar */}
          <div>
            <Label htmlFor="dayPillar">日柱</Label>
            <select
              id="dayPillar"
              value={formData.dayPillar || ""}
              onChange={(e) => handleChange("dayPillar", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">请选择</option>
              {getPillarOptions().map(option => (
                <option key={`day-${option}`} value={option}>{option}</option>
              ))}
            </select>
            {errors.dayPillar && (
              <p className="text-sm text-red-500 mt-1">{errors.dayPillar}</p>
            )}
          </div>

          {/* Hour Pillar */}
          <div>
            <Label htmlFor="hourPillar">时柱</Label>
            <select
              id="hourPillar"
              value={formData.hourPillar || ""}
              onChange={(e) => handleChange("hourPillar", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">请选择</option>
              {getPillarOptions().map(option => (
                <option key={`hour-${option}`} value={option}>{option}</option>
              ))}
            </select>
            {errors.hourPillar && (
              <p className="text-sm text-red-500 mt-1">{errors.hourPillar}</p>
            )}
          </div>
        </div>
      </div>

      {/* DaYun Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-500" />
          大运信息
        </h3>
        <p className="text-sm text-muted-foreground">
          大运决定人生的大趋势，是命理预测的重要依据
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Start Age */}
          <div>
            <Label htmlFor="startAge">起运年龄（虚岁）</Label>
            <Input
              id="startAge"
              type="number"
              value={formData.startAge || ""}
              onChange={(e) => handleChange("startAge", e.target.value)}
              placeholder="如 8"
              min={1}
              max={12}
            />
            {errors.startAge && (
              <p className="text-sm text-red-500 mt-1">{errors.startAge}</p>
            )}
          </div>

          {/* First DaYun */}
          <div>
            <Label htmlFor="firstDaYun">第一步大运</Label>
            <select
              id="firstDaYun"
              value={formData.firstDaYun || ""}
              onChange={(e) => handleChange("firstDaYun", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">请选择</option>
              {getPillarOptions().map(option => (
                <option key={`dayun-${option}`} value={option}>{option}</option>
              ))}
            </select>
            {errors.firstDaYun && (
              <p className="text-sm text-red-500 mt-1">{errors.firstDaYun}</p>
            )}
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="pt-4">
        <Button
          type="submit"
          size="lg"
          disabled={isSubmitting}
          className="w-full md:w-auto gap-2"
        >
          <Sparkles className="w-5 h-5" />
          {isSubmitting ? "生成中..." : "生成人生K线"}
        </Button>
      </div>
    </form>
  );
}
